import { useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { jwtDecode } from 'jwt-decode';
import { PATHS } from '@/utils/constants';

interface TokenPayload {
  exp: number;
  iat: number;
}

export const useTokenExpiration = () => {
  const router = useRouter();

  const checkTokenExpiration = useCallback(async () => {
    try {
      const response = await fetch('/api/refreshToken', {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        console.error('Token refresh failed, redirecting to login');
        router.push(PATHS.LOGIN);
        return;
      }

      const cookies = document.cookie.split(';').reduce(
        (acc, cookie) => {
          const [key, value] = cookie.trim().split('=');
          acc[key] = value;
          return acc;
        },
        {} as Record<string, string>,
      );

      const accessToken = cookies.accessToken;
      if (accessToken) {
        try {
          const decoded = jwtDecode<TokenPayload>(accessToken);
          const now = Math.floor(Date.now() / 1000);
          const timeUntilExpiry = decoded.exp - now;

          if (timeUntilExpiry <= 300) {
            const refreshResponse = await fetch('/api/refreshToken', {
              method: 'GET',
              credentials: 'include',
            });

            if (!refreshResponse.ok) {
              console.error('Failed to refresh expiring token');
              router.push(PATHS.LOGIN);
            }
          }
        } catch (error) {
          console.error('Error decoding token:', error);
        }
      }
    } catch (error) {
      console.error('Error checking token expiration:', error);
    }
  }, [router]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      return;
    }

    const hideTokenCheck =
      router.pathname === PATHS.LOGIN ||
      router.pathname === PATHS.SESSION_EXPIRED ||
      router.pathname === PATHS.REAUTH;

    if (hideTokenCheck) {
      return;
    }

    checkTokenExpiration();

    const interval = setInterval(checkTokenExpiration, 60000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [checkTokenExpiration, router.pathname]);

  return {
    checkTokenExpiration,
  };
};
